import { type GroupedSubjects } from '@/shared/category/type';

export const CATEGORY_SEARCH_SUGGESTION_MIN_QUERY_LENGTH = 2;

export type CategorySearchSuggestionType = 'subject' | 'sub_subject';

export type CategorySearchSuggestion = {
  type: CategorySearchSuggestionType;
  groupId: string;
  subjectGroupId: string;
  subSubjectGroupId?: string;
  displayName: string;
  matchedName: string;
  order: number;
  searchTargets: string[];
  hasSubSubjects: boolean;
};

export const formatCategorySuggestionQuizCount = (
  count: number | string | undefined,
) => {
  if (typeof count !== 'number') return '';

  return `${new Intl.NumberFormat('ko-KR').format(count)}개`;
};

const normalizeCategorySearchText = (text: string) =>
  text.trim().replace(/\s+/g, '').toLocaleLowerCase();

const getMatchRank = (searchTargets: string[], normalizedQuery: string) => {
  const normalizedTargets = searchTargets.map(normalizeCategorySearchText);

  if (normalizedTargets.some(target => target === normalizedQuery)) return 0;
  if (normalizedTargets.some(target => target.startsWith(normalizedQuery))) {
    return 1;
  }
  if (normalizedTargets.some(target => target.includes(normalizedQuery))) {
    return 2;
  }

  return Number.POSITIVE_INFINITY;
};

const canShowSuggestion = (
  suggestion: CategorySearchSuggestion,
  normalizedQuery: string,
) => {
  const matchRank = getMatchRank(suggestion.searchTargets, normalizedQuery);

  if (matchRank === Number.POSITIVE_INFINITY) return false;

  // 기존 텍스트 검색은 1글자 submit을 막는다. 자동완성도 noisy 노출을
  // 피하되, 실제 1글자 카테고리명이 exact match인 경우만 예외 허용한다.
  if (normalizedQuery.length < CATEGORY_SEARCH_SUGGESTION_MIN_QUERY_LENGTH) {
    return matchRank === 0;
  }

  return true;
};

export const getCategorySearchSuggestions = (
  subjectGroups: GroupedSubjects,
  query: string,
  limit = 4,
): CategorySearchSuggestion[] => {
  const normalizedQuery = normalizeCategorySearchText(query);

  if (!normalizedQuery) return [];

  return Object.entries(subjectGroups)
    .flatMap(([mainSubjectGroupId, mainSubject]) => {
      const hasOwnSubSubjects = Object.keys(mainSubject.children).some(
        childGroupId => childGroupId !== mainSubjectGroupId,
      );

      const subjectSuggestion: CategorySearchSuggestion = {
        type: 'subject',
        groupId: mainSubjectGroupId,
        subjectGroupId: mainSubjectGroupId,
        displayName: mainSubject.name,
        matchedName: mainSubject.name,
        order: mainSubject.order * 1000,
        searchTargets: [mainSubject.name],
        hasSubSubjects: hasOwnSubSubjects,
      };

      const subSubjectSuggestions = Object.entries(mainSubject.children)
        .filter(([childGroupId]) => childGroupId !== mainSubjectGroupId)
        .map(([childGroupId, child]) => {
          const displayName = `${mainSubject.name} ${child.name}`;

          return {
            type: 'sub_subject' as const,
            groupId: childGroupId,
            subjectGroupId: mainSubjectGroupId,
            subSubjectGroupId: childGroupId,
            displayName,
            matchedName: child.name,
            order: mainSubject.order * 1000 + child.order,
            searchTargets: [displayName, child.name],
            hasSubSubjects: false,
          };
        });

      return [subjectSuggestion, ...subSubjectSuggestions];
    })
    .filter(suggestion => canShowSuggestion(suggestion, normalizedQuery))
    .sort((a, b) => {
      const rankA = getMatchRank(a.searchTargets, normalizedQuery);
      const rankB = getMatchRank(b.searchTargets, normalizedQuery);

      if (rankA !== rankB) return rankA - rankB;
      return a.order - b.order;
    })
    .slice(0, limit);
};
